import { useFieldArray, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateStockInOrder,
  getListStockInOrdersQueryKey,
  type StockInOrder,
} from "@workspace/api-client-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { VendorCombobox } from "./vendor-combobox";
import { ItemCombobox } from "./item-combobox";
import { uploadInvoice, type UploadResult } from "@/lib/upload";
import { useState } from "react";

// Phase 22-06 — Create a Draft stock-in order (prepaid split-delivery).
// The form collects: vendor (FK or free-text), expected delivery window, notes,
// and 1-N cart lines (item + optional variant + qty + expected unit price).
// No GST on the cart — cart holds pre-tax expected value only (CONTEXT.md).

// R2/R3 — per-line lifecycle status. 'Personal' marks a line bought for personal
// use that will never produce a stock-in. The other terminal statuses (Delivered
// / Returned / Refunded) are usually reached later from the order detail sheet,
// but admins can set them up-front (e.g. opening-balance, immediate-return).
const CART_LINE_STATUSES = [
  "Ordered",
  "In_Progress",
  "Delivered",
  "Returned",
  "Refunded",
  "Personal",
] as const;
type CartLineStatus = (typeof CART_LINE_STATUSES)[number];

interface CartLineValues {
  itemId: number | null;
  variantId?: number | null;
  quantityOrdered: number | string;
  expectedUnitPrice: number | string;
  notes: string;
  status: CartLineStatus;
}

interface FormValues {
  vendorId: number | null;
  vendorNameFreeText: string;
  expectedDeliveryFrom: string;
  expectedDeliveryTo: string;
  notes: string;
  referenceId: string;
  orderProofUrl: string | null;
  cartLines: CartLineValues[];
}

interface StockInOrderFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (order: StockInOrder) => void;
}

export function StockInOrderForm({ open, onOpenChange, onSaved }: StockInOrderFormProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const form = useForm<FormValues>({
    defaultValues: {
      vendorId: null,
      vendorNameFreeText: "",
      expectedDeliveryFrom: "",
      expectedDeliveryTo: "",
      notes: "",
      referenceId: "",
      orderProofUrl: null,
      cartLines: [
        { itemId: null, variantId: null, quantityOrdered: 1, expectedUnitPrice: "", notes: "", status: "Ordered" },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "cartLines",
  });

  const createMut = useCreateStockInOrder({
    mutation: {
      onSuccess: (order) => {
        qc.invalidateQueries({ queryKey: getListStockInOrdersQueryKey() });
        toast({ title: "Order created." });
        form.reset();
        onSaved(order);
      },
      onError: () => {
        toast({ title: "Failed to create order. Please try again.", variant: "destructive" });
      },
    },
  });

  function onSubmit(values: FormValues) {
    const lines = values.cartLines.filter((l) => l.itemId != null);
    if (lines.length === 0) {
      toast({ title: "Add at least one cart line.", variant: "destructive" });
      return;
    }

    createMut.mutate({
      data: {
        vendorId: values.vendorId ?? undefined,
        vendorNameFreeText: values.vendorNameFreeText || undefined,
        expectedDeliveryFrom: values.expectedDeliveryFrom || undefined,
        expectedDeliveryTo: values.expectedDeliveryTo || undefined,
        notes: values.notes || undefined,
        referenceId: values.referenceId || undefined,
        orderProofUrl: values.orderProofUrl ?? undefined,
        cartLines: lines.map((l) => ({
          itemId: l.itemId!,
          variantId: l.variantId ?? undefined,
          quantityOrdered: Number(l.quantityOrdered),
          expectedUnitPrice: Number(l.expectedUnitPrice),
          notes: l.notes || undefined,
          status: l.status,
        })),
      },
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Purchase Order</SheetTitle>
          <p className="text-sm text-muted-foreground">
            Create a draft prepaid order. Payment is recorded after review.
          </p>
        </SheetHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-5">
          {/* Vendor */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Vendor</label>
            <VendorCombobox
              value={form.watch("vendorId")}
              onChange={(id) => form.setValue("vendorId", id)}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Or vendor free text (if not in system)
            </label>
            <Input
              placeholder="Vendor name…"
              {...form.register("vendorNameFreeText")}
            />
          </div>

          {/* Expected delivery window */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Expected delivery from</label>
              <Input type="date" {...form.register("expectedDeliveryFrom")} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Expected delivery to</label>
              <Input type="date" {...form.register("expectedDeliveryTo")} />
            </div>
          </div>

          {/* Reference + proof — operator-entered external reference (vendor PO
              number, e-commerce order id, …) and an uploaded order proof. */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Reference ID</label>
            <Input placeholder="Vendor PO #, e-comm order id, …" {...form.register("referenceId")} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Order proof</label>
            <OrderProofUploader
              value={form.watch("orderProofUrl")}
              onChange={(url) => form.setValue("orderProofUrl", url)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              placeholder="Internal notes…"
              rows={2}
              {...form.register("notes")}
            />
          </div>

          <Separator />

          {/* Cart lines */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Cart Lines</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({ itemId: null, variantId: null, quantityOrdered: 1, expectedUnitPrice: "", notes: "", status: "Ordered" })
                }
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add line
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Expected prices are pre-tax. Actual amounts are confirmed at delivery.
            </p>

            {fields.map((field, idx) => (
              <div key={field.id} className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Line {idx + 1}</span>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={() => remove(idx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Item</label>
                  <ItemCombobox
                    value={form.watch(`cartLines.${idx}.itemId`) ?? null}
                    onChange={(id) => form.setValue(`cartLines.${idx}.itemId`, id)}
                    showTier={false}
                    placeholder="Search item…"
                    className="w-full"
                    // RECIPE-DISPLAY-02: this is a Stock-In entry point (new order cart-line picker) —
                    // recipe items are cooked to order, never received into inventory.
                    excludeRecipe
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">Qty</label>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      placeholder="1"
                      {...form.register(`cartLines.${idx}.quantityOrdered`)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">Expected unit price (₹)</label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      {...form.register(`cartLines.${idx}.expectedUnitPrice`)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">Status</label>
                    <Select
                      value={form.watch(`cartLines.${idx}.status`)}
                      onValueChange={(v) =>
                        form.setValue(`cartLines.${idx}.status`, v as CartLineStatus)
                      }
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CART_LINE_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">Line notes</label>
                    <Input
                      placeholder="Optional…"
                      {...form.register(`cartLines.${idx}.notes`)}
                    />
                  </div>
                </div>
                {form.watch(`cartLines.${idx}.status`) === "Personal" && (
                  <p className="text-xs text-amber-700">
                    Personal items don't enter stock and won't appear when recording deliveries.
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={createMut.isPending}
              className="flex-1"
            >
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Draft Order
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset();
                onOpenChange(false);
              }}
            >
              Discard
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// Inline order-proof upload tile. Reuses the existing uploadInvoice helper
// (Supabase Storage 'invoices' bucket) — same storage class as the delivery
// invoice; both are vendor-issued PO/order docs.
export function OrderProofUploader({ value, onChange }: { value: string | null | undefined; onChange: (v: string | null) => void }) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  async function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r: UploadResult = await uploadInvoice(f);
      if (r.ok) { onChange(r.url); toast({ title: "Order proof uploaded." }); }
      else toast({ title: r.message, variant: "destructive" });
    } finally { setUploading(false); e.target.value = ""; }
  }
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm underline truncate flex-1">View order proof</a>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <label className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${uploading ? "pointer-events-none opacity-60" : ""}`}>
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      <span>{uploading ? "Uploading…" : "Upload order proof"}</span>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="sr-only" onChange={handle} disabled={uploading} />
    </label>
  );
}
